import { normalizeFieldJson, normalizeFieldText } from '~/composables/useCustomFields'
import {
  appendCdnImageIdentifier,
  normalizeCdnImageIdentifierMap,
  resolveCdnImageIdentifier
} from '~/utils/cdnImageIdentifier.mjs'

type SiteDataState = {
  setting?: {
    customFields?: Record<string, unknown>
  }
} | null

const STATIC_RESOURCE_PREFIXES = ['/uploads/', '/usr/uploads/', '/static/', '/assets/']

function parseApiBase(apiBase: string) {
  const value = String(apiBase || '/api/v1').trim().replace(/\/+$/, '')
  if (!value) {
    return {
      path: '/api/v1',
      origin: '',
      absolute: false
    }
  }
  if (value.startsWith('http://') || value.startsWith('https://')) {
    const parsed = new URL(value)
    const path = parsed.pathname.replace(/\/+$/, '') || '/api/v1'
    return {
      path,
      origin: parsed.origin,
      absolute: true
    }
  }
  return {
    path: `/${value.replace(/^\/+/, '')}`,
    origin: '',
    absolute: false
  }
}

function getCdnDomain() {
  const siteData = useState<SiteDataState>('site-data')
  return normalizeFieldText(siteData.value?.setting?.customFields, 'cdnDomain', '').replace(/\/+$/, '')
}

function getCdnImageIdentifierMap() {
  const siteData = useState<SiteDataState>('site-data')
  const mapping = normalizeFieldJson<Record<string, unknown>>(
    siteData.value?.setting?.customFields,
    'cdnImageIdentifier',
    {}
  )
  return normalizeCdnImageIdentifierMap(mapping)
}

function getSiteUrl() {
  const siteData = useState<SiteDataState>('site-data')
  return normalizeFieldText(siteData.value?.setting?.customFields, 'siteUrl', '').replace(/\/+$/, '')
}

function normalizeHost(hostname: string) {
  return String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '')
}

function isStaticResourcePath(pathname: string) {
  const value = String(pathname || '')
  return STATIC_RESOURCE_PREFIXES.some(prefix => value.startsWith(prefix))
}

function replaceAbsoluteSiteAssetUrl(input: string) {
  const value = String(input || '').trim()
  if (!/^https?:\/\//i.test(value)) {
    return value
  }

  const cdnDomain = getCdnDomain()
  const siteUrl = getSiteUrl()
  if (!cdnDomain || !siteUrl) {
    return value
  }

  let parsedAsset: URL
  let parsedSite: URL
  let parsedCdn: URL
  try {
    parsedAsset = new URL(value)
    parsedSite = new URL(siteUrl)
    parsedCdn = new URL(cdnDomain)
  } catch {
    return value
  }

  if (normalizeHost(parsedAsset.hostname) !== normalizeHost(parsedSite.hostname)) {
    return value
  }
  if (!isStaticResourcePath(parsedAsset.pathname)) {
    return value
  }

  const cdnBasePath = parsedCdn.pathname.replace(/\/+$/, '')
  const resourcePath = `/${parsedAsset.pathname.replace(/^\/+/, '')}`
  const mergedPath = cdnBasePath ? `${cdnBasePath}${resourcePath}` : resourcePath
  return `${parsedCdn.origin}${mergedPath}${parsedAsset.search}${parsedAsset.hash}`
}

function appendImageIdentifier(input: string) {
  const value = String(input || '').trim()
  if (!value || (!/^https?:\/\//i.test(value) && !value.startsWith('//'))) {
    return value
  }
  const mapping = getCdnImageIdentifierMap()
  return appendCdnImageIdentifier(value, resolveCdnImageIdentifier(value, mapping))
}

type ResolveAssetUrlOptions = {
  image?: boolean
}

export function resolveAssetUrl(input: string | undefined | null, options: ResolveAssetUrlOptions = {}) {
  const value = String(input || '').trim()
  if (!value) {
    return ''
  }

  if (value.startsWith('#')) {
    return value
  }

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value)) {
    const resolved = replaceAbsoluteSiteAssetUrl(value)
    return options.image ? appendImageIdentifier(resolved) : resolved
  }

  if (value.startsWith('//')) {
    return options.image ? appendImageIdentifier(value) : value
  }

  const api = parseApiBase(String(useRuntimeConfig().public.apiBase || '/api/v1'))
  const cdnDomain = getCdnDomain()
  const normalizedInput = `/${value.replace(/^\/+/, '')}`
  const sitePath = normalizedInput === api.path || normalizedInput.startsWith(`${api.path}/`)
    ? normalizedInput
    : `${api.path}/${normalizedInput.replace(/^\/+/, '')}`

  if (cdnDomain) {
    const resolved = `${cdnDomain}${sitePath}`
    return options.image ? appendImageIdentifier(resolved) : resolved
  }
  if (api.absolute) {
    const resolved = `${api.origin}${sitePath}`
    return options.image ? appendImageIdentifier(resolved) : resolved
  }
  return options.image ? appendImageIdentifier(sitePath) : sitePath
}

export function resolveImageAssetUrl(input: string | undefined | null) {
  return resolveAssetUrl(input, { image: true })
}

export function resolveHtmlAssetUrls(input: string | undefined | null) {
  const html = String(input || '')
  if (!html) {
    return ''
  }

  const replaceAttr = (source: string, pattern: RegExp, resolver: (value: string) => string = resolveAssetUrl) =>
    source.replace(pattern, (_: string, prefix: string, src: string, suffix: string) => `${prefix}${resolver(src)}${suffix}`)

  const replaceSrcset = (source: string) =>
    source.replace(/(<(?:img|source)\b[^>]*\ssrcset=['"])([^'"]+)(['"][^>]*>)/gi, (_: string, prefix: string, srcset: string, suffix: string) => {
      const next = srcset
        .split(',')
        .map(item => {
          const value = item.trim()
          if (!value) {
            return value
          }
          const [src, descriptor] = value.split(/\s+/, 2)
          const resolved = resolveImageAssetUrl(src)
          return descriptor ? `${resolved} ${descriptor}` : resolved
        })
        .join(', ')
      return `${prefix}${next}${suffix}`
    })

  const replaceInlineUrl = (source: string) =>
    source.replace(/url\((['"]?)([^'")]+)\1\)/gi, (_: string, quote: string, url: string) => `url(${quote}${resolveAssetUrl(url)}${quote})`)

  const ensureImgAttrs = (source: string) =>
    source.replace(/<img\b[^>]*>/gi, (tag: string) => {
      let next = tag
      const hasClass = /\sclass=['"]([^'"]*)['"]/i.exec(next)
      if (hasClass) {
        const classText = hasClass[1] || ''
        if (!/\bblog-progressive-img\b/i.test(classText)) {
          const merged = `${classText} blog-progressive-img is-progressive`.trim()
          next = next.replace(hasClass[0], ` class="${merged}"`)
        } else if (!/\bis-progressive\b/i.test(classText)) {
          const merged = `${classText} is-progressive`.trim()
          next = next.replace(hasClass[0], ` class="${merged}"`)
        }
      } else {
        next = next.replace(/<img\b/i, '<img class="blog-progressive-img is-progressive"')
      }
      if (!/\sloading=['"][^'"]*['"]/i.test(next)) {
        next = next.replace(/<img\b/i, '<img loading="lazy"')
      }
      if (!/\sdecoding=['"][^'"]*['"]/i.test(next)) {
        next = next.replace(/<img\b/i, '<img decoding="async"')
      }
      return next
    })

  let output = html
  output = replaceAttr(output, /(<img\b[^>]*\ssrc=['"])([^'"]+)(['"][^>]*>)/gi, resolveImageAssetUrl)
  output = replaceAttr(output, /(<script\b[^>]*\ssrc=['"])([^'"]+)(['"][^>]*>)/gi)
  output = replaceAttr(output, /(<source\b[^>]*\ssrc=['"])([^'"]+)(['"][^>]*>)/gi)
  output = replaceAttr(output, /(<video\b[^>]*\sposter=['"])([^'"]+)(['"][^>]*>)/gi, resolveImageAssetUrl)
  output = replaceAttr(output, /(<link\b[^>]*\shref=['"])([^'"]+)(['"][^>]*>)/gi)
  output = replaceSrcset(output)
  output = replaceInlineUrl(output)
  output = ensureImgAttrs(output)
  return output
}
